import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  CalendarIcon,
  UserIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';

export default function AdminCommunicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [communication, setCommunication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchCommunication();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, navigate]);

  const fetchCommunication = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/communications/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      console.log('Communication reçue:', response.data);
      setCommunication(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération:', error);
      alert('Erreur lors de la récupération de la communication');
      navigate('/admin/communications');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'envoye': 'bg-green-100 text-green-800',
      'brouillon': 'bg-gray-100 text-gray-800',
      'programme': 'bg-blue-100 text-blue-800'
    };
    const labels = {
      'envoye': 'Envoyé',
      'brouillon': 'Brouillon',
      'programme': 'Programmé'
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badges[statut]}`}>
        {labels[statut]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!communication) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate('/admin/communications')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour aux communications
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {communication.titre}
              </h1>
              {getStatutBadge(communication.statut)}
              {communication.estSanitaire && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  Intérêt général
                  {communication.nombreDestinataires !== undefined && (
                    <span className="ml-1.5 text-xs">
                      ({communication.nombreDestinataires} destinataire{communication.nombreDestinataires > 1 ? 's' : ''})
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contenu principal */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Contenu du message</h2>
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: communication.contenu }}
              />
            </div>

            {communication.piecesJointes?.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6 mt-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Pièces jointes</h2>
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                  {communication.piecesJointes.map((pj) => (
                    <li key={pj._id} className="flex items-center justify-between px-3 py-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const response = await axios.get(
                              `${process.env.REACT_APP_API_URL}/communications/${communication._id}/pieces-jointes/${pj._id}`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (response.data?.url) {
                              window.open(response.data.url, '_blank');
                            }
                          } catch (error) {
                            if (pj.url) {
                              window.open(pj.url, '_blank');
                              return;
                            }
                            alert('Impossible de télécharger ce fichier');
                          }
                        }}
                        className="flex items-center min-w-0 text-left text-sm text-indigo-700 hover:text-indigo-900"
                      >
                        <PaperClipIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{pj.nomOriginal || pj.nom}</span>
                      </button>
                      {pj.taille != null && (
                        <span className="ml-3 text-xs text-gray-400 flex-shrink-0">
                          {pj.taille < 1024 * 1024
                            ? `${Math.round(pj.taille / 1024)} Ko`
                            : `${(pj.taille / (1024 * 1024)).toFixed(1)} Mo`}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Informations latérales */}
          <div className="space-y-6">
            {/* Informations générales */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Informations</h3>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Auteur</div>
                    <div className="text-sm text-gray-600">
                      {communication.auteur?.prenom} {communication.auteur?.nom}
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Destinataires</div>
                    <div className="text-sm text-gray-600">
                      {communication.estSanitaire
                        ? 'Tous les adhérents (alertes sanitaires)'
                        : [
                            ...(communication.criteresDestinataires || []).map((c) => c.organisme),
                            ...(communication.groupesDestinataires || []).map((g) =>
                              `${g.organisme} ${g.groupe === 'bureau' ? 'Bureau' : g.organisme === 'SAR' ? 'CS' : 'CA'}`
                            ),
                          ].filter((v, i, a) => a.indexOf(v) === i).join(' · ') || 'Aucun destinataire'
                      }
                    </div>
                  </div>
                </div>

                {communication.statut === 'envoye' && communication.dateEnvoi && (
                  <div className="flex items-start">
                    <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Date d'envoi</div>
                      <div className="text-sm text-gray-600">
                        {formatDate(communication.dateEnvoi)}
                      </div>
                    </div>
                  </div>
                )}

                {communication.statut === 'programme' && communication.dateProgrammee && (
                  <div className="flex items-start">
                    <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Date programmée</div>
                      <div className="text-sm text-gray-600">
                        {formatDate(communication.dateProgrammee)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Statistiques d'envoi */}
            {communication.statut === 'envoye' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Statistiques d'envoi</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">Envoyés</span>
                    </div>
                    <span className="text-lg font-semibold text-green-600">
                      {communication.emailsEnvoyes}
                    </span>
                  </div>

                  {communication.emailsEchoues > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                        <span className="text-sm text-gray-600">Échecs</span>
                      </div>
                      <span className="text-lg font-semibold text-red-600">
                        {communication.emailsEchoues}
                      </span>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Total</span>
                      <span className="text-lg font-bold text-gray-900">
                        {communication.emailsEnvoyes + communication.emailsEchoues}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Erreurs */}
                {communication.erreurs && communication.erreurs.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">
                      Dernières erreurs ({communication.erreurs.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {communication.erreurs.map((err, index) => (
                        <div key={index} className="text-xs bg-red-50 p-2 rounded">
                          <div className="font-medium text-red-900">{err.email}</div>
                          <div className="text-red-700">{err.erreur}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
